import {Nullish} from 'shared/web';

import {EitherQuantityOrError} from '../types/basket/EitherQuantityOrError';
import {IdentifiableBasketItem} from '../types/basket/IdentifiableBasketItem';
import {useInvalidBasketItemsIds} from './useInvalidBasketItemsIds';
import {usePendingQuantityChangeIds} from './usePendingQuantityChangeIds';

/**
 * Aggregates the quantity-input state of all basket items for the component that owns
 * the basket actions.
 *
 * Every quantity report goes through the single `setBasketItemQuantity` setter, so both
 * tracked states are always updated together.
 *
 * `hasUnsettledQuantity` covers both reasons a quantity is not safe to commit yet - it is
 * invalid, or it has not reached the server yet - and is what actions that turn the basket
 * into a document must be disabled by.
 */
export function useBasketItemsQuantityState(basketItems: IdentifiableBasketItem[] | Nullish) {
  const {setInvalidBasketItemId, hasInvalidBasketItems} = useInvalidBasketItemsIds(basketItems);
  const {setPendingQuantityChangeItemId, hasPendingQuantityChange} =
    usePendingQuantityChangeIds(basketItems);

  const setBasketItemQuantity = (itemId: string, quantity: EitherQuantityOrError) => {
    setInvalidBasketItemId(itemId, quantity);
    setPendingQuantityChangeItemId(itemId, quantity);
  };

  const hasUnsettledQuantity = hasInvalidBasketItems || hasPendingQuantityChange;

  return {
    setBasketItemQuantity,
    hasInvalidBasketItems,
    hasPendingQuantityChange,
    hasUnsettledQuantity,
  };
}
